import { Label } from '@radix-ui/react-label';
import {
  Card,
  Checkbox,
  Flex,
  IconButton,
  Popover,
  Spinner,
  Text,
} from '@radix-ui/themes';
import { AddPitchIcon } from 'components/common/custom-icon/shorthands';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import { GameDataPlayerAvatar } from 'components/sections/game-data/players/avatar';
import { GameDataPitcherRepertoire } from 'components/sections/game-data/players/pitcher-repertoire';
import { GameDataTeamLogo } from 'components/sections/game-data/team-logo';
import { GameDataContext } from 'contexts/game-data.context';
import { CheckedContext } from 'contexts/layout/checked.context';
import { GameDataTab } from 'enums/game-data.enums';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMlbPitchExt } from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import { IMlbPlayer } from 'lib_ts/interfaces/mlb-stats-api/i-player';
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { MlbStatsService } from 'services/mlb-stats.service';
import './card.scss';

export const GameDataPlayerCard = (props: {
  player: IMlbPlayer;
  mode: GameDataTab;
}) => {
  const navigate = useNavigate();
  const { getChecked, setChecked } = useContext(CheckedContext);

  const [hover, setHover] = useState(false);

  const { mergeFilterPitches } = useContext(GameDataContext);

  const { player } = props;

  const teamName = useMemo(() => {
    const { _mx_team_details } = player;

    if (_mx_team_details?.code && _mx_team_details?.shortName) {
      return `${_mx_team_details?.code} ${_mx_team_details?.shortName}`;
    }

    if (_mx_team_details?.name) {
      return _mx_team_details?.name;
    }

    return 'Inactive';
  }, [player._mx_team_details]);

  const rgb = StringHelper.hexToRgb(player._mx_team_details?.colorHex);

  const [pitches, setPitches] = useState<IMlbPitchExt[]>();

  const checkboxID = `cb-${props.player.playerPk}`;

  return (
    <Card
      key={player.playerPk}
      size="2"
      className="GameDataPlayer cursor-pointer animate-background"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={
        hover
          ? {
              background:
                `radial-gradient(` +
                ` 201.63% 141.42% at 100% 100%,` +
                ` rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.25) 0%,` +
                ` rgba(0, 0, 0, 0.00) 100%),` +
                ` var(--Panel-default, rgba(29, 29, 33, 0.70)` +
                `)`,
            }
          : undefined
      }
      onClick={() => {
        // reset filters from previous interactions
        mergeFilterPitches({
          isHome: undefined,
          pitchType: undefined,
          inning: undefined,
          outcomeType: undefined,
          batSide: undefined,
          pitchHand: undefined,
          gamePks: undefined,
          hitterPks: undefined,
          pitcherPks: undefined,
        });
        navigate(
          props.mode === 'hitters'
            ? paths.pitches.gameData.hitter(player.playerPk.toString())
            : paths.pitches.gameData.pitcher(player.playerPk.toString())
        );
      }}
    >
      <Flex gap="2" justify="between" align="center">
        {props.mode === 'pitchers' && (
          <Label
            htmlFor={checkboxID}
            className="CheckboxWrapperLabel"
            // avoid click from opening the player
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              id={checkboxID}
              checked={getChecked(props.player._id)}
              // avoid click from opening the player
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={(v) =>
                setChecked([props.player._id], v !== false)
              }
            />
          </Label>
        )}

        <GameDataPlayerAvatar
          size="4"
          name={player.name}
          playerPk={player.playerPk}
        />

        <Flex direction="column" gap="1" flexGrow="1" overflow="hidden">
          <Text size="3" title={player.name} truncate>
            {player.name}
          </Text>

          <Flex gap="2">
            <GameDataTeamLogo
              size="20px"
              teamPK={player._mx_team_details?.teamPk}
              name={player._mx_team_details?.name}
            />

            <Text color={RADIX.COLOR.SECONDARY} title={teamName} truncate>
              {teamName}
            </Text>
          </Flex>
        </Flex>

        {props.mode === 'pitchers' && (
          <PitcherHandBadge value={player.pitchHand} />
        )}

        {props.mode === 'hitters' && <HitterSideBadge value={player.batSide} />}

        {props.mode === 'pitchers' && (
          <Popover.Root
            onOpenChange={(open) => {
              if (!open) {
                // avoid taking too much memory if a bunch of different repertoires are opened/closed
                setPitches(undefined);
              }
            }}
          >
            <Popover.Trigger>
              <IconButton
                className="btn-floating"
                variant={RADIX.BUTTON.VARIANT.BORDERLESS}
                color={RADIX.COLOR.NEUTRAL}
                onClick={async (e) => {
                  e.stopPropagation();

                  if (pitches) {
                    return;
                  }

                  const built =
                    await MlbStatsService.getInstance().getPlayerPitches({
                      mode: 'pitcher',
                      player: player,
                    });

                  setPitches(built);
                }}
              >
                <AddPitchIcon />
              </IconButton>
            </Popover.Trigger>
            <Popover.Content asChild>
              <Card
                onClick={(e) => {
                  // prevent clicking on the card from opening the pitcher page
                  e.stopPropagation();
                }}
              >
                {pitches ? (
                  <GameDataPitcherRepertoire
                    pitcher={props.player}
                    pitches={pitches}
                  />
                ) : (
                  <Spinner />
                )}
              </Card>
            </Popover.Content>
          </Popover.Root>
        )}
      </Flex>
    </Card>
  );
};
